/**
 * Phase 3 + 9bis-d — Portail Client Hub (`/lead/:hub_token/hub`).
 *
 * Page publique, aucun JWT. Charge l'aggregate via GET /api/public/hub/{token}
 * qui renvoie depuis 9bis-a un `projects[]` (multi-projet par contact) + rétrocompat
 * `quotes/invoices/chantiers` à plat.
 *
 * Comportement :
 * - 1 projet : rendu "à plat" via <ProjectSection mode="flat">.
 * - N projets : chaque projet dans un <ProjectSection mode="accordion">
 *   (1er ouvert par défaut, autres ouverts si N ≤ 2).
 * - Rétrocompat legacy : si `data.projects` est vide/absent mais `data.quotes` /
 *   `data.invoices` / `data.chantiers` non vides, on construit un pseudo-projet
 *   unique pour préserver le rendu.
 *
 * Design : palette crème/brun cohérente avec l'espace client existant,
 * accent color = company.primary_color. Responsive iPad landscape + mobile.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Download, Loader2, AlertTriangle } from "lucide-react";
import BankDetailsModal from "@/components/BankDetailsModal";
import ProjectSection from "@/components/ProjectSection";

const API = process.env.REACT_APP_BACKEND_URL;

export default function HubPage() {
  const { hub_token: hubToken } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [invoiceModal, setInvoiceModal] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/public/hub/${hubToken}`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.status === 404 ? "invalid" : "error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hubToken]);

  const accent = data?.company?.primary_color || "#C4553B";

  // Résolution du contact (nouveau shape 9bis-a) avec fallback lead legacy
  const contactInfo = useMemo(() => {
    if (!data) return null;
    const c = data.contact || data.lead || {};
    return {
      first_name: c.first_name || "",
      last_name: c.last_name || "",
    };
  }, [data]);

  // Construit la liste des projets à afficher, avec fallback rétrocompat
  const projects = useMemo(() => {
    if (!data) return [];
    if (Array.isArray(data.projects) && data.projects.length > 0) {
      return data.projects;
    }
    // Fallback legacy : construit un projet virtuel à partir des listes à plat
    const flatQuotes = data.quotes || [];
    const flatInvoices = data.invoices || [];
    const flatChantiers = data.chantiers || data.projects || [];
    if (flatQuotes.length + flatInvoices.length + flatChantiers.length === 0) return [];
    return [
      {
        id: "legacy",
        title: "Votre projet",
        stage: null,
        quotes: flatQuotes,
        invoices: flatInvoices,
        chantiers: flatChantiers,
      },
    ];
  }, [data]);

  const isMultiProject = projects.length > 1;

  if (error === "invalid") {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-[#F7F3EC] p-6"
        data-testid="hub-invalid-page"
      >
        <div className="max-w-md rounded-lg border border-[#E5DDD1] bg-white p-8 text-center">
          <AlertTriangle className="mx-auto h-10 w-10 text-[#C4553B]" />
          <h1 className="mt-4 text-2xl font-bold text-[#2A2420]">Lien invalide</h1>
          <p className="mt-2 text-sm text-[#5B5148]">
            Ce lien n&apos;est plus valide ou a été régénéré. Contactez votre interlocuteur pour recevoir un nouveau lien.
          </p>
        </div>
      </div>
    );
  }
  if (error === "error") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F7F3EC] p-6">
        <p className="text-sm text-[#5B5148]">Une erreur est survenue. Merci de réessayer plus tard.</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F7F3EC]">
        <Loader2 className="h-6 w-6 animate-spin text-[#5B5148]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F3EC] text-[#2A2420]" data-testid="hub-page">
      {/* Header */}
      <header className="border-b border-[#E5DDD1] bg-white">
        <div className="mx-auto flex max-w-4xl flex-col gap-4 px-4 py-6 sm:flex-row sm:items-center sm:gap-6 sm:px-6 sm:py-8">
          {(data.company.logo_file_id || data.company.logo_url) ? (
            <img
              src={
                data.company.logo_file_id
                  ? `${API}/public/logos/${data.company.logo_file_id}`
                  : data.company.logo_url.startsWith("http")
                  ? data.company.logo_url
                  : `${API}${data.company.logo_url}`
              }
              alt={data.company.name}
              className="h-14 w-auto max-w-[200px] object-contain"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <div
              className="flex h-14 w-14 items-center justify-center rounded font-bold text-white"
              style={{ background: accent }}
            >
              {data.company.name?.[0]?.toUpperCase()}
            </div>
          )}
          <div className="flex-1">
            <h1 className="text-2xl font-bold sm:text-3xl" data-testid="hub-greeting">
              Bonjour {contactInfo?.first_name || ""}
            </h1>
            <p className="mt-1 text-sm text-[#5B5148]" data-testid="hub-subtitle">
              {isMultiProject ? (
                <>
                  Retrouvez ici tous les documents liés à <b>vos projets</b> avec{" "}
                  <b>{data.company.name}</b>.
                </>
              ) : (
                <>
                  Retrouvez ici tous les documents liés à <b>votre projet</b> avec{" "}
                  <b>{data.company.name}</b>.
                </>
              )}
            </p>
            {isMultiProject && (
              <p className="mt-1 text-xs text-[#5B5148]" data-testid="hub-projects-count">
                {projects.length} projets en cours ou clôturés
              </p>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl space-y-8 px-4 py-8 sm:px-6">
        {projects.length === 0 ? (
          <div
            className="rounded-lg border border-dashed border-[#D9CFC1] bg-white p-8 text-center text-sm text-[#5B5148]"
            data-testid="hub-empty-state"
          >
            Aucun projet pour le moment. Votre interlocuteur reviendra vers vous très prochainement.
          </div>
        ) : isMultiProject ? (
          <div className="space-y-4" data-testid="hub-projects-container">
            {projects.map((p, idx) => (
              <ProjectSection
                key={p.id}
                project={p}
                accent={accent}
                companyIban={data.company.iban}
                onOpenBankModal={setInvoiceModal}
                mode="accordion"
                defaultOpen={projects.length <= 2 || idx === 0}
              />
            ))}
          </div>
        ) : (
          <ProjectSection
            project={projects[0]}
            accent={accent}
            companyIban={data.company.iban}
            onOpenBankModal={setInvoiceModal}
            mode="flat"
          />
        )}

        {/* Documents utiles (globaux au contact) */}
        {data.company.cgv_url && (
          <section data-testid="hub-docs-section">
            <div className="mb-3 flex items-center gap-2">
              <h2 className="text-lg font-semibold text-[#2A2420]">Documents utiles</h2>
            </div>
            <Button
              asChild
              variant="outline"
              className="border-[#D9CFC1] bg-white text-[#2A2420] hover:bg-[#F0EAE0]"
            >
              <a
                href={`${API}${data.company.cgv_url}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hub-cgv-download"
              >
                <Download className="mr-1 h-3.5 w-3.5" /> Télécharger nos CGV
              </a>
            </Button>
          </section>
        )}

        {/* Footer */}
        <footer className="mt-12 border-t border-[#E5DDD1] pt-6 text-center text-xs text-[#5B5148]">
          <div className="mb-2 text-sm text-[#5B5148]">
            {data.company.name}
            {data.company.phone && <span> · {data.company.phone}</span>}
            {data.company.email && <span> · {data.company.email}</span>}
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="/legal/mentions" className="hover:underline" data-testid="hub-legal-link">
              Mentions légales
            </a>
            {data.opt_out_url && (
              <a
                href={data.opt_out_url}
                className="hover:underline"
                data-testid="hub-optout-link"
              >
                Ne plus recevoir de communications marketing
              </a>
            )}
          </div>
        </footer>
      </main>

      <BankDetailsModal
        open={!!invoiceModal}
        onOpenChange={(o) => !o && setInvoiceModal(null)}
        companyName={data.company.name}
        ibanFormatted={data.company.iban_formatted}
        ibanRaw={data.company.iban}
        structuredComm={invoiceModal?.structured_comm}
        accent={accent}
      />
    </div>
  );
}
